import { AnimatePresence } from "motion/react";
import type React from "react";
import { twJoin } from "tailwind-merge";

import type { AdoptionRateDetailsDto } from "@/api/types";
import { ActivityDot } from "@/components/ActivityDot/ActivityDot";
import { ArrowNarrowRightIcon } from "@/components/Icons/ArrowNarrowRightIcon";
import { TrendDown01Icon } from "@/components/Icons/TrendDown01Icon";
import { TrendUp01Icon } from "@/components/Icons/TrendUp01Icon";
import { Badge } from "@/design-system/Badge";
import { Button } from "@/design-system/Button";
import { cardSurfaceVariants } from "@/design-system/Card";
import { Stack } from "@/design-system/Stack";
import { useTranslation } from "@/translations";

import { AnalyticsWidgetStates } from "./AnalyticsWidgetStates";
import { getProjectPeriodLabel } from "./helpers";

interface WidgetCardProps {
  title: string;
  description?: string;
  href?: string;
  children: React.ReactNode;
  isLoading: boolean;
  isError: boolean;
  isDataAvailable: boolean;
  error: Error | null;
  onReload?: () => void;
  shouldFillHeight?: boolean;
  isHighlighted?: boolean;
}

function WidgetCardRoot({
  title,
  description,
  href,
  children,
  isLoading,
  isError,
  isDataAvailable,
  error,
  onReload,
  shouldFillHeight = true,
  isHighlighted = false,
}: WidgetCardProps): React.ReactNode {
  const { t } = useTranslation();

  const listTitleWords = title.split(" ");

  return (
    <article
      className={twJoin(
        "relative flex w-full flex-col items-center gap-space-md p-space-md @xl:p-space-lg",
        cardSurfaceVariants(),
        shouldFillHeight ? "h-full" : "h-fit",
      )}
    >
      {/* Title & Description */}
      <section className="flex w-full items-start justify-between gap-space-sm">
        <Stack>
          <h3 className="text-heading-3">
            {/* This is needed to make sure the highlight indicator follows the last word of the title */}
            {listTitleWords.map((word, index) => {
              const isLastWord = index === listTitleWords.length - 1;
              const formattedWord = `${word}${isLastWord ? "" : " "}`;

              return (
                <span className="relative" key={word}>
                  <span>{formattedWord}</span>
                  {isHighlighted && isLastWord && (
                    <span className="absolute top-0 -right-space-sm translate-x-full">
                      <ActivityDot />
                    </span>
                  )}
                </span>
              );
            })}
          </h3>
          {description && <p className="text-body text-text-secondary">{description}</p>}
        </Stack>
        {href && (
          <Button type="link" variant="ghostPrimary" href={href}>
            {t("common.action.see-more")}
          </Button>
        )}
      </section>
      <section className="relative size-full">
        <section
          className={twJoin("flex size-full flex-col gap-space-lg transition-opacity", !isDataAvailable && "opacity-0")}
        >
          {children}
        </section>
        {/* Error & Empty states should co-exist with the title */}
        <AnimatePresence>
          {isError && <AnalyticsWidgetStates.Error {...{ error, onReload }} />}
          {!isLoading && !isError && !isDataAvailable && <AnalyticsWidgetStates.Empty />}
        </AnimatePresence>
      </section>
      {/* Loading state should be overlaying the entire widget */}
      <AnimatePresence>{isLoading && <AnalyticsWidgetStates.Loading />}</AnimatePresence>
    </article>
  );
}

type WidgetCardBodyProps = {
  children: React.ReactNode;
  align?: "center" | "stretch";
};

function WidgetCardBody({ children, align = "center" }: WidgetCardBodyProps): React.ReactNode {
  return (
    <section
      className={twJoin(
        "relative flex w-full flex-col justify-center gap-space-md",
        align === "center" ? "items-center" : "items-stretch",
      )}
    >
      {children}
    </section>
  );
}

function WidgetCardFooter({ children }: { children: React.ReactNode }): React.ReactNode {
  return <footer className="mt-auto flex w-full flex-col items-center gap-space-lg">{children}</footer>;
}

type WidgetCardBenchmarkInfoProps = {
  benchmark: number;
  period: AdoptionRateDetailsDto["period"];
};

const WidgetCardBenchmarkInfo = ({ benchmark, period }: WidgetCardBenchmarkInfoProps): React.ReactNode => {
  const { t } = useTranslation();

  return (
    <Stack>
      <h4 className="text-heading-4">
        {t("component.analytics-widget.common.aop-benchmark.label", {
          score: `${benchmark}%`,
        })}
      </h4>
      <p className="text-body text-text-secondary">
        {t("component.analytics-widget.adoption.description", {
          period: getProjectPeriodLabel(t, period),
        })}
      </p>
    </Stack>
  );
};

type WidgetReferenceLabelProps = {
  currValue: number;
  referenceValue: number;
};

const WidgetReferenceLabel = ({ currValue, referenceValue }: WidgetReferenceLabelProps): React.ReactNode => {
  const { t } = useTranslation();

  const difference = currValue - referenceValue;
  let label = t("component.analytics-widget.adoption.trend.neutral.description");
  let icon = <ArrowNarrowRightIcon />;
  let variant: "success" | "warning" | "info" = "info";
  if (difference > 0) {
    icon = <TrendUp01Icon />;
    variant = "success";
    label = t("component.analytics-widget.adoption.trend.positive.description", {
      amount: `${difference}%`,
    });
  } else if (difference < 0) {
    icon = <TrendDown01Icon />;
    variant = "warning";
    label = t("component.analytics-widget.adoption.trend.negative.description", {
      amount: `${difference}%`,
    });
  }

  return (
    <Badge size="lg" icon={icon} {...{ variant }}>
      {label}
    </Badge>
  );
};

export const AnalyticsWidgetCard = {
  Root: WidgetCardRoot,
  Body: WidgetCardBody,
  Footer: WidgetCardFooter,
  ReferenceLabel: WidgetReferenceLabel,

  BenchmarkInfo: WidgetCardBenchmarkInfo,
};
